// @ts-strict-ignore
import { Dictionary } from 'typescript-collections';
import { Group, Matrix4, Mesh, MeshPhongMaterial, Object3D, Vector3 } from 'three';
import { WxCarrierElementMesh } from './wx-carrier-element-mesh';
import { WxDrawingElement } from './wx-drawing-element';
import { WxText } from './shape/wx-text';
import { WxShape } from './shape/wx-shape';
import { WxConnectionElement } from './wx-connection-element';
import { WxConnectionPlaneMesh } from './wx-connection-plane-mesh';
import { ExportToolUtils } from './export-tool-utils';
import { WxArticleVO } from '@article/model/vo/wx-article.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { CommonConstants } from '@3d-scene/constants/common-constants';
import { Face3D } from '@3d-scene/model/face-3d';
import { FaceUtils } from '@3d-scene/common/util/face-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';

export class WxDrawingElementMesh extends Group {

  protected article: WxArticleVO;

  public constructor(article: WxArticleVO) {
    super();
    this.article = article;
  }

  public async invalidateMesh(exportToolUtils: ExportToolUtils, parseConnections: boolean): Promise<void> {
    if (!this.article) {
      return;
    }
    try {
      const poleCount: number = this.article.poleCount;
      const poleX: number = this.article.shape.poleX;
      const poleY: number = this.article.shape.poleY;
      const poleZ: number = this.article.shape.poleZ;

      for (let i: number = 0; i < poleCount; i++) {
        if (parseConnections && ArrayUtils.isNotEmpty(this.article.connections)) {
          // filter duplicated connection-elements:
          // duplicate connection entries stem from joining table ConnectionGroup_Connection,
          // a connection can belong to more than one group at a time, for each group membership a result entry is produced
          const connectionDic: Dictionary<string, boolean> = new Dictionary();

          for (const wxConnectionVO of this.article.connections) {
            const connection: WxConnectionElement = new WxConnectionElement(wxConnectionVO);
            //1134 Fixing First/Middle/Last
            if ((connection.first && i == 0) ||
              (connection.last && i == poleCount - 1) ||
              (connection.middle && i > 0 && i < poleCount - 1)) {
              connection.poleIndex = i;
              const key: string = String(connection.connectionId) + '_' + String(connection.connectionOrder);
              if (!connectionDic.getValue(key)) {
                const connectionElementObj: Object3D = await this.createConnectionElement(exportToolUtils, connection);
                if (connectionElementObj) {
                  const transformMatrix: Matrix4 = ExportToolUtils.translateToConfiguratorTransformation(i * poleX + connection.coordinateX,
                    i * poleY + connection.coordinateY, i * poleZ + connection.coordinateZ,
                    connection.rotationX, connection.rotationY, connection.rotationZ);
                  connectionElementObj.applyMatrix4(transformMatrix);
                  this.add(connectionElementObj);
                  if (!connectionElementObj.name) {
                    connectionElementObj.name = key;
                  }
                  connectionDic.setValue(key, true);
                }
              }
            }
          }
        }

        for (const drawingElement of this.article.shape.drawingElements) {
          if (this.parent instanceof WxCarrierElementMesh && i > 1) {
            continue;
          }
          //due to 1027 we should recolor drawing element with article color
          const de: WxDrawingElement = new WxDrawingElement(drawingElement);
          if (NumberUtils.isNumber(drawingElement.shapeRgb1) && drawingElement.shapeRgb1 > 0) {
            de.rgb1 = drawingElement.shapeRgb1;
          } else if (NumberUtils.isNumber(this.article.rgb1) && this.article.rgb1 > 0) {
            de.rgb1 = this.article.rgb1;
          }

          if (drawingElement.shapeTexture) {
            de.texture = drawingElement.shapeTexture;
          } else if (this.article.texture) {
            de.texture = this.article.texture;
          }
          const drawingElementObj: Mesh = await this.createDrawingElement(exportToolUtils, de);
          if (drawingElementObj != null) {
            const transformMatrix: Matrix4 = ExportToolUtils.translateToConfiguratorTransformation(i * poleX + drawingElement.coordinateX,
              i * poleY + drawingElement.coordinateY, i * poleZ + drawingElement.coordinateZ,
              drawingElement.rotationX, drawingElement.rotationY, drawingElement.rotationZ);
            drawingElementObj.geometry.applyMatrix4(transformMatrix);
            this.add(drawingElementObj);
          }
        }
      }
      return;
    } catch (e) {
      console.log('DrawingElementMesh#invalidateMesh failed on ' + this.article.itemId);
      throw e;
    }
  }

  public async createDrawingElement(exportToolUtils: ExportToolUtils, drawingElement: WxDrawingElement): Promise<Mesh> {
    const scaleXYZ: Vector3 = ExportToolUtils.translateToConfiguratorCoords(drawingElement.mirrorX, drawingElement.mirrorY, drawingElement.mirrorZ);
    let mesh: Mesh = null;
    const material: MeshPhongMaterial = ExportToolUtils.createColorMaterial(drawingElement.rgb1);

    for (const shapeElement of drawingElement.elements) {
      if (shapeElement instanceof WxText) {
        mesh = await exportToolUtils.createTextMesh(shapeElement, drawingElement).toPromise();
      } else if (shapeElement instanceof WxShape) {
        const closed: boolean = shapeElement.closed;
        const points: Vector3[] = shapeElement.getPoints();
        if (drawingElement.mirror) {
          points.reverse();
        }

        if (scaleXYZ.length() === 0) {
          scaleXYZ.z = -0.01;
        }

        mesh = ExportToolUtils.createExtrudeMesh(points, scaleXYZ, closed, material);
        if (mesh) {
          if (drawingElement.texture === 'texture.bmp') {
            await this.applyBitmapMaterial(mesh, 'texture.png');
          } else if (drawingElement.texture === 'texture1.bmp') {
            await this.applyBitmapMaterial(mesh, 'texture1.png');
          } else if (drawingElement.texture === 'texture2.bmp') {
            await this.applyBitmapMaterial(mesh, 'texture2.png');
          }
          mesh.name = 's' + drawingElement.drawingID + '-' + drawingElement.elementOrder;
        }
      }
    }
    return mesh;
  }

  private applyBitmapMaterial(mesh: Mesh, imageName: string): Promise<void> {
    return new Promise<void>((resolve) => {
      const image = new Image();
      let loaded: boolean = false;
      image.onload = () => {
        if (!loaded) {
          loaded = true;
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.height = image.naturalHeight;
          canvas.width = image.naturalWidth;
          ctx.drawImage(image, 0, 0);
          image.src = canvas.toDataURL('image/png');
          ExportToolUtils.applyImageToMeshMaterial(image, mesh);
          resolve();
        }
      };
      image.onerror = () => {
        resolve();
      };
      image.src = 'ng-assets/img/textures/' + imageName;
    });
  }

  private async createConnectionElement(exportToolUtils: ExportToolUtils, connection: WxConnectionElement): Promise<Object3D> {
    const connectionPlaneMesh: WxConnectionPlaneMesh = new WxConnectionPlaneMesh(connection);
    await connectionPlaneMesh.invalidateMesh(exportToolUtils);
    const mesh: Mesh = connectionPlaneMesh.firstFlatMesh;

    const offset: number = CommonConstants.EPSILON;
    const faces: Face3D[] = FaceUtils.getFaces(mesh);
    const face: Face3D = faces[0];
    const normal: Vector3 = MathUtils.getNormalFromFirstPoint(face.vertices[0], face.vertices[1], face.vertices[2]);
    normal.applyQuaternion(mesh.quaternion);

    const offsetX: number = normal.x * offset;
    const offsetY: number = normal.y * offset;
    const offsetZ: number = normal.z * offset;

    mesh.position.add(new Vector3(offsetX, offsetY, offsetZ));

    return connectionPlaneMesh;
  }
}
